import type { FallbackProps } from 'react-error-boundary'
import { useNavigate, useParams } from 'react-router'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klear/ui/components/card'
import { redirectToLogin } from '@klear/ui/lib/auth-redirect'
import { ROUTE_TRADER, buildNavPathTrader } from '@klear/constants'
import klearLogo from '@/assets/logo-klear.png'

type TraderErrorFallbackProps = FallbackProps & {
  variant?: 'full' | 'content'
}

export function TraderErrorFallback({ error, resetErrorBoundary, variant = 'full' }: TraderErrorFallbackProps) {
  const navigate = useNavigate()
  const { traderId } = useParams()

  if (error.message === 'Unauthorized') {
    redirectToLogin()
    return null
  }

  const goHome = () => {
    if (traderId) {
      navigate(buildNavPathTrader(traderId))
    } else {
      navigate(`/${ROUTE_TRADER}`)
    }
  }

  const containerClass =
    variant === 'full'
      ? 'flex flex-col items-center justify-center min-h-screen bg-background'
      : 'flex flex-col items-center justify-center flex-1 min-h-[24rem] rounded-lg border border-dashed bg-muted/30 p-8'

  return (
    <div className={containerClass}>
      {variant === 'full' && (
        <a href="/">
          <img
            alt="Klear Logo"
            width="180"
            height="48"
            decoding="async"
            className="h-12 w-auto mb-8 dark:invert"
            src={klearLogo}
            style={{ color: 'transparent' }}
          />
        </a>
      )}
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold">Something went wrong</CardTitle>
          <CardDescription>
            We&apos;ve notified our team and they&apos;ll look into it. You can try again or return to the home page.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button className="w-full" onClick={resetErrorBoundary}>
            Try again
          </Button>
          <Button className="w-full" variant="outline" onClick={goHome}>
            Go to home
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
